import {
    AppSettingsState,
    SymbologyValues,
} from 'app/store/appSettings/AppSettingsState';
import State from 'store/State';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { StreamRequestInfo } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { WidgetConfigWithSymbol } from 'widgets/WidgetSettingsEditing/WidgetSettingsSymbolEditor';
import { WidgetId } from 'widgets/WidgetId';
import { selectWidgetInstanceConfig } from 'widgets/store/selectors';
import { selectConfiguredSymbolForWidgetOrItsCapsuleContainer } from 'widgets/tiles/store/selectors';
import { DataServices } from 'store/Services';

export interface SymbolDependentRequestHelpers<
    TRequest,
    TConfig extends WidgetConfigWithSymbol,
> {
    getWidgetStreamRequestInfo: (
        state: State,
        services: DataServices,
        id: WidgetId
    ) => Promise<StreamRequestInfo<TRequest> | undefined>;
    createWidgetRequestUid: (
        state: State,
        services: DataServices,
        id: WidgetId
    ) => Promise<string | undefined>;
    getStreamRequestInfosForSymbols: (
        state: State,
        services: DataServices,
        symbols: string[],
        config: TConfig,
        quantities?: number[]
    ) => Promise<StreamRequestInfo<TRequest>[]>;
    buildStreamRequest: (
        state: State,
        services: DataServices,
        id: WidgetId
    ) => Promise<TRequest | undefined>;
}

const defaultExtractSymbolFromPayload = <TRequest>(r: TRequest) => {
    return (r as { symbol?: string }).symbol;
};
/*
 * Builds helper functions for requests that use a symbol from config, or symbols supplied from a MyPortfolioCapsules widgets
 */
export function buildSymbolDependentRequestHelpers<
    TRequest,
    TConfig extends WidgetConfigWithSymbol,
    TExtraArgs = void,
>(
    toRequestUid: (
        symbol: string,
        config: TConfig,
        qty?: number,
        extraArgs?: TExtraArgs
    ) => string,
    requestToStreamKey: (request: TRequest) => string,
    /*
     * The function to build the request for a symbol/symbology and config
     * Note that the function should use the supplied symbol and not attempt to extract the symbol from the config
     * Config is there for other request parameters eg buckerSize, numWindows etc
     */
    buildRequest: (
        symbol: string,
        symbology: SymbologyValues,
        config: TConfig,
        qty?: number,
        extraArgs?: TExtraArgs
    ) => TRequest | undefined,
    initialConfig: TConfig,
    extractSymbolFromPayload: (
        r: TRequest
    ) => string | undefined = defaultExtractSymbolFromPayload,
    buildExtraArgs?: (
        state: State,
        services: DataServices,
        config: TConfig,
        symbol?: string
    ) => Promise<TExtraArgs>
): SymbolDependentRequestHelpers<TRequest, TConfig> {
    async function buildStreamRequestFromPortfolioSelection(
        state: State,
        services: DataServices,
        portfolioSelection: PortfolioSelectionWrapper,
        config: TConfig
    ): Promise<TRequest | undefined> {
        const symbol = portfolioSelection.selectedSymbol;
        const symbology = portfolioSelection.symbology!;
        const qty = portfolioSelection.selectedSymbolQuantity?.qty;

        const extraArgs = buildExtraArgs
            ? await buildExtraArgs(state, services, config, symbol)
            : undefined;

        return symbol
            ? buildRequest(symbol, symbology, config, qty, extraArgs)
            : undefined;
    }

    async function buildStreamRequestFromConfig(
        id: WidgetId,
        appSettings: AppSettingsState,
        state: State,
        services: DataServices
    ): Promise<TRequest | undefined> {
        const config = selectWidgetInstanceConfig(state, id) ?? initialConfig;
        const symbol = selectConfiguredSymbolForWidgetOrItsCapsuleContainer(
            state,
            id
        );

        const symbology = appSettings.symbology!;

        const extraArgs = buildExtraArgs
            ? await buildExtraArgs(state, services, config, symbol)
            : undefined;

        return symbol
            ? buildRequest(symbol, symbology, config, undefined, extraArgs)
            : undefined;
    }

    async function buildStreamRequest(
        state: State,
        services: DataServices,
        id: WidgetId
    ) {
        const appSettings = selectAppSettings(state);
        const requestFromConfig = await buildStreamRequestFromConfig(
            id,
            appSettings,
            state,
            services
        );
        if (requestFromConfig) {
            return requestFromConfig;
        }

        const selection = selectPortfolioSelection(state);
        const selectionWrapper = new PortfolioSelectionWrapper(selection);

        return await buildStreamRequestFromPortfolioSelection(
            state,
            services,
            selectionWrapper,
            selectWidgetInstanceConfig(state, id) ?? initialConfig
        );
    }

    async function getWidgetStreamRequestInfo(
        state: State,
        services: DataServices,
        id: WidgetId
    ): Promise<StreamRequestInfo<TRequest> | undefined> {
        const payload = await buildStreamRequest(state, services, id);
        if (!payload) {
            return Promise.resolve(undefined);
        }
        const symbol = extractSymbolFromPayload(payload);
        if (!symbol) {
            return Promise.resolve(undefined);
        }

        const config = selectWidgetInstanceConfig(state, id) ?? initialConfig;

        const extraArgs = buildExtraArgs
            ? await buildExtraArgs(state, services, config, symbol)
            : undefined;

        const streamKey = toRequestUid(
            symbol,
            selectWidgetInstanceConfig(state, id) ?? initialConfig,
            (payload as { qty?: number }).qty,
            extraArgs
        );
        return { streamKey, payload };
    }

    async function createWidgetRequestUid(
        state: State,
        services: DataServices,
        id: WidgetId
    ) {
        const request = await buildStreamRequest(state, services, id);
        if (!request) {
            return undefined;
        }
        return requestToStreamKey(request);
    }

    async function getStreamRequestInfosForSymbols(
        state: State,
        services: DataServices,
        symbols: string[],
        config: TConfig,
        quantities?: number[]
    ): Promise<StreamRequestInfo<TRequest>[]> {
        const { symbology } = selectAppSettings(state);

        const promises = symbols.map(async (symbol, index) => {
            const extraArgs = buildExtraArgs
                ? await buildExtraArgs(state, services, config, symbol)
                : undefined;
            const qty = quantities ? quantities[index] : undefined;
            return {
                streamKey: toRequestUid(symbol, config, qty, extraArgs),
                payload: buildRequest(
                    symbol,
                    symbology,
                    config,
                    qty,
                    extraArgs
                )!,
            };
        });
        return Promise.all(promises);
    }

    return {
        buildStreamRequest,
        getWidgetStreamRequestInfo,
        createWidgetRequestUid,
        getStreamRequestInfosForSymbols,
    };
}
